import type { PageContextPayload } from "./page-context"

export interface PageContextPosterOptions {
  detectSku: () => string | null
  readPath: () => string
  post: (context: PageContextPayload) => void
  watch: (
    detect: () => PageContextPayload,
    post: (context: PageContextPayload) => void,
  ) => () => void
}

export interface PageContextPoster {
  enable(): void
  setOverride(sku: string | null): void
  reset(): void
  detect(): PageContextPayload
  readonly enabled: boolean
}

export function createPageContextPoster({
  detectSku,
  readPath,
  post,
  watch,
}: PageContextPosterOptions): PageContextPoster {
  let override: string | null = null
  let lastPosted: PageContextPayload | null = null
  let enabled = false
  let unwatch: (() => void) | null = null

  const detect = (): PageContextPayload => ({
    sku: override ?? detectSku(),
    pathname: readPath(),
  })

  const deliver = (
    context: PageContextPayload,
    { force = false }: { force?: boolean } = {},
  ) => {
    if (
      !force &&
      lastPosted &&
      lastPosted.sku === context.sku &&
      lastPosted.pathname === context.pathname
    ) {
      return
    }
    lastPosted = context
    post(context)
  }

  return {
    get enabled() {
      return enabled
    },

    detect,

    enable() {
      if (enabled) return
      enabled = true
      deliver(detect(), { force: true })
      unwatch = watch(detect, (context) => deliver(context))
    },

    setOverride(sku: string | null) {
      override = sku
      if (!enabled) return
      deliver(detect(), { force: true })
    },

    reset() {
      unwatch?.()
      unwatch = null
      enabled = false
      override = null
      lastPosted = null
    },
  }
}
